import React from "react";
import { Link } from "react-router-dom";

const PrivacyPolicy = () => {
  const effectiveDate = "November 12, 2025";

  return (
    <div className="min-h-screen bg-background text-foreground">
      <div className="mx-auto w-full max-w-4xl px-6 py-16">
        <div className="mb-10">
          <Link
            to="/"
            className="inline-flex items-center text-sm font-semibold text-primary hover:underline"
          >
            ← Back to DoodleMagic
          </Link>
        </div>
        <header className="space-y-4">
          <span className="text-sm font-semibold uppercase tracking-widest text-muted-foreground">
            Privacy Policy
          </span>
          <h1 className="font-heading text-4xl font-bold">
            How The LFG Company protects your data
          </h1>
          <p className="text-base text-muted-foreground">
            Effective date: {effectiveDate}
          </p>
        </header>

        <section className="mt-10 space-y-6 text-base leading-relaxed text-muted-foreground">
          <p>
            The LFG Company (&ldquo;we,&rdquo; &ldquo;us,&rdquo; or &ldquo;our&rdquo;)
            operates DoodleMagic, a service that turns children&rsquo;s drawings
            into animated videos for families and guardians. This Privacy Policy
            describes how we collect, use, share, and protect information when
            you use our website and mobile applications (collectively, the
            &ldquo;Service&rdquo;).
          </p>

          <section className="space-y-4">
            <h2 className="font-heading text-2xl font-semibold text-foreground">
              Information we collect
            </h2>
            <ul className="list-disc space-y-2 pl-6">
              <li>
                <strong className="text-foreground">Account data:</strong>{" "}
                Name, email address, and password (if applicable) provided when
                you create or manage an account.
              </li>
              <li>
                <strong className="text-foreground">Payment data:</strong>{" "}
                Credit or debit card details and related billing information
                handled securely by Stripe on our behalf. We do not store full
                card numbers.
              </li>
              <li>
                <strong className="text-foreground">User content:</strong>{" "}
                Uploaded drawings, descriptions, prompts, and generated videos
                that you create within the studio.
              </li>
              <li>
                <strong className="text-foreground">Usage data:</strong>{" "}
                Log data, device information, and cookies or similar
                technologies captured via Google Cloud Platform (hosting) and
                Supabase (backend/auth) to operate and improve the Service.
              </li>
              <li>
                <strong className="text-foreground">AI processing data:</strong>{" "}
                Drawings and prompts submitted for video generation are
                transmitted to Google&rsquo;s Gemini API and Veo3. Generated
                outputs may be stored in your account.
              </li>
            </ul>
          </section>

          <section className="space-y-4">
            <h2 className="font-heading text-2xl font-semibold text-foreground">
              How we use the information
            </h2>
            <ul className="list-disc space-y-2 pl-6">
              <li>Provide, maintain, and improve the Service and your account.</li>
              <li>
                Process transactions and send related communications such as
                receipts or reminders.
              </li>
              <li>
                Generate animations and other outputs from the content you
                provide.
              </li>
              <li>Detect, prevent, or address fraud, security, or technical issues.</li>
              <li>Communicate about updates, support, or marketing (opt out anytime).</li>
              <li>Comply with legal obligations and enforce our terms.</li>
            </ul>
          </section>

          <section className="space-y-4">
            <h2 className="font-heading text-2xl font-semibold text-foreground">
              Sharing of information
            </h2>
            <p>
              We do not sell personal information. We share data only with:
            </p>
            <ul className="list-disc space-y-2 pl-6">
              <li>
                <strong className="text-foreground">Service providers:</strong>{" "}
                Stripe (payments), Supabase (database/auth), Google Cloud
                Platform (hosting/storage), and Google Gemini/Veo3 (AI
                processing). They may use data solely to deliver the Service.
              </li>
              <li>
                <strong className="text-foreground">Legal compliance:</strong>{" "}
                When required by law, regulation, legal process, or to protect
                rights, property, or safety.
              </li>
            </ul>
          </section>

          <section className="space-y-4">
            <h2 className="font-heading text-2xl font-semibold text-foreground">
              Data retention
            </h2>
            <p>
              We retain personal data until you request deletion or close your
              account. We may adopt a 30-day auto-deletion policy for dormant
              content in the future and will update this Privacy Policy if that
              changes. Residual copies may remain in backups and logs for a
              limited period.
            </p>
          </section>

          <section className="space-y-4">
            <h2 className="font-heading text-2xl font-semibold text-foreground">
              Your rights (California &amp; other jurisdictions)
            </h2>
            <p>
              Residents of California (CPRA/CCPA) and certain other regions may
              have the right to:
            </p>
            <ul className="list-disc space-y-2 pl-6">
              <li>Access the categories and specific pieces of personal information we hold.</li>
              <li>Request deletion of personal information, subject to legal exceptions.</li>
              <li>Correct inaccurate personal information.</li>
              <li>
                Opt out of &ldquo;sharing&rdquo; for cross-context behavioral
                advertising (we currently do not engage in this practice).
              </li>
              <li>
                Be free from discrimination for exercising any privacy rights.
              </li>
            </ul>
            <p>
              Submit requests through our{" "}
              <Link to="/support" className="text-primary underline">
                Support Page
              </Link>
              . We will verify your identity before fulfilling a request.
            </p>
          </section>

          <section className="space-y-4">
            <h2 className="font-heading text-2xl font-semibold text-foreground">
              Children&rsquo;s privacy
            </h2>
            <p>
              DoodleMagic is designed for parents and guardians. If users under
              18 create accounts, they must do so with parental consent. We do
              not knowingly collect personal information directly from children
              under 13 without verifiable parental consent. If you believe we
              have collected such information, contact us through the{" "}
              <Link to="/support" className="text-primary underline">
                Support Page
              </Link>{" "}
              and we will delete it.
            </p>
          </section>

          <section className="space-y-4">
            <h2 className="font-heading text-2xl font-semibold text-foreground">
              Security
            </h2>
            <p>
              We implement reasonable administrative, technical, and physical
              safeguards&mdash;including encrypted connections and restricted
              access&mdash;to help protect your information. However, no system
              is completely secure, and we cannot guarantee absolute protection.
            </p>
          </section>

          <section className="space-y-4">
            <h2 className="font-heading text-2xl font-semibold text-foreground">
              International users
            </h2>
            <p>
              The Service is operated in the United States. By using DoodleMagic,
              you agree that your data may be processed in the U.S., where
              privacy laws may differ from those in your jurisdiction.
            </p>
          </section>

          <section className="space-y-4">
            <h2 className="font-heading text-2xl font-semibold text-foreground">
              Changes to this policy
            </h2>
            <p>
              We may update this Privacy Policy from time to time. When we make
              material changes, we will update the effective date and provide
              notice through the Service. Your continued use after changes means
              you accept the updated policy.
            </p>
          </section>

          <section className="space-y-4">
            <h2 className="font-heading text-2xl font-semibold text-foreground">
              Contact
            </h2>
            <p>
              For privacy questions, access or deletion requests, or complaints,
              please visit our{" "}
              <Link to="/support" className="text-primary underline">
                Support Page
              </Link>
              .
            </p>
          </section>
        </section>
      </div>
    </div>
  );
};

export default PrivacyPolicy;

